<template>
  <mi-form
    class="windchill-filter"
    @submit.prevent="exportToWindchill"
  >
    <div class="windchill-filter--container">
      <!-- Configuration Settings  -->
      <configuration-windchill-settings
        v-model:selected-option-set-id="selectedOptionSetId"
        :option-sets="optionSets"
        :windchill-id="windchillId"
      ></configuration-windchill-settings>

      <!-- Filtering Criterias  -->
      <configuration-windchill-filtering-criterias
        v-model:structure-primary-criteria="filterStructurePrimaryCriteria"
        v-model:structure-secondary-criteria="filterStructureSecondaryCriteria"
        v-model:yellow-primary-criteria="filterYellowPrimaryCriteria"
        :product-model="productModel"
      >
      </configuration-windchill-filtering-criterias>
    </div>

    <div class="windchill-filter--actions">
      <mi-btn flat @click="handleBackClick"> Back </mi-btn>
      <mi-btn type="submit">
        <span v-if="hasWindchillData"> Update </span>
        <span v-else> Export </span>
      </mi-btn>
    </div>
  </mi-form>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex'
  import {
    createWindchillFilter,
    getCreateWindchillFilter
  } from '@/api'
  import ConfigurationWindchillSettings
    from '@/components/pac/configuration/windchill-interface/ConfigurationWindchillSettings.vue'
  import ConfigurationWindchillFilteringCriterias
    from '@/components/pac/configuration/windchill-interface/ConfigurationWindchillFilteringCriterias.vue'
  import recordAnalytics from '@/utils/analytics/recordAnalytics'
  import { PAC_WINDCHILL_VARIANT_EXPORT } from '@/utils/analytics/constants'
  import { poll } from '@/api/polling'
  import notify from '@/utils/notify'

  const { mapActions, mapState, mapGetters } = createNamespacedHelpers('pac')

  export default {
    name: 'ConfigurationWindchillFilter',
    components: { ConfigurationWindchillSettings, ConfigurationWindchillFilteringCriterias },
    props: {
      optionSets: {
        type: Array,
        required: true
      }
    },
    emits: ['back:windchill', 'status:windchill-export', 'async:clear-poll'],
    data: () => ({
      filterStructurePrimaryCriteria: '',
      filterStructureSecondaryCriteria: false,
      filterYellowPrimaryCriteria: '',
      productModel: '',
      selectedOptionSetId: ''
    }),
    computed: {
      ...mapState(['currentConfiguration', 'selectedChoices']),
      ...mapGetters(['configStructureOptionsPerProductModel', 'configSpecificationFilterOptionsPerProductModel']),
      windchillId() {
        return this?.currentConfiguration?.windChillData?.filterOid
      },
      hasWindchillData() {
        return !!this.currentConfiguration.windChillData && this.currentConfiguration.windChillData.filterOid
      }
    },
    created() {
      this.productModel = this.currentConfiguration.productModel.businessName
      this.filterStructurePrimaryCriteria = this.structureOptionsRecommendedPerProductModel(this.productModel)
      this.filterYellowPrimaryCriteria = this.configSpecFilterOptionsRecommendedPerProductModel(this.productModel)
    },
    methods: {
      ...mapActions(['loadConfiguration', 'saveConfiguration']),
      structureOptionsRecommendedPerProductModel(productModel) {
        return this.configStructureOptionsPerProductModel(productModel)
          .find(structureOption => structureOption.recommended === true).name
      },
      configSpecFilterOptionsRecommendedPerProductModel(productModel) {
        return this.configSpecificationFilterOptionsPerProductModel(productModel)
          .find(structureOption => structureOption.recommended === true).name
      },
      handleBackClick() {
        this.$emit('back:windchill')
      },
      async exportToWindchill() {
        const request = this.buildFilterRequest()

        // save configuration
        try {
          this.$emit('status:windchill-export', { isSavingConfiguration: true })

          await this.saveConfiguration(this.currentConfiguration)
        }
        catch {
          this.$emit('status:windchill-export', { isSavingConfiguration: false })
          return
        }

        // export to windchill
        try {
          this.$emit('status:windchill-export', { isSavingConfiguration: false, isExportingToWindchill: true })

          await this.getUnusedCreateWindchillFilter(request)
          await this.loadConfiguration(this.currentConfiguration.id)
        }
        catch {
          this.$emit('status:windchill-export', { isExportingToWindchill: false })
          return
        }

        // export to windchill successful
        this.$emit('status:windchill-export', { isExportingToWindchill: false, isExportSuccessful: true })

        // Analytics
        recordAnalytics(PAC_WINDCHILL_VARIANT_EXPORT)
      },
      async getUnusedCreateWindchillFilter(request) {
        const { executePoll, clearPoll } = poll()
        this.$emit('async:clear-poll', { clearPoll })

        try {
          await executePoll({
            correlationIdEndpoint: {
              getCorrelationId: createWindchillFilter,
              params: request
            },
            asyncEndpoint: getCreateWindchillFilter
          })
        }
        catch (e) {
          notify({
            title: `HTTP Error ${ e.status || '' }`,
            content: e.message || 'Fetch PAC Windchill Filter',
            type: 'negative'
          })
          throw e
        }
      },
      buildFilterRequest() {
        const optionsAndChoices = Array.from(this.selectedChoices).map(entry => ({
          optionId: entry[0],
          choiceNumbers: entry[1].map(choice => choice.id2)
        }))
        const request = {
          configurationSpecificationStructure: {
            isLatestDesign: this.filterStructureSecondaryCriteria,
            unitEffectivity: [{
              context: this.filterStructurePrimaryCriteria,
              effectivityValue: this.currentConfiguration.planningPeriodRange.from.substring(2)
            }]
          },
          configurationSpecificationOptionFilter: {
            unitEffectivity: [{
              context: this.filterYellowPrimaryCriteria,
              effectivityValue: this.currentConfiguration.planningPeriodRange.from.substring(2)
            }]
          },
          optionSetOid: this.selectedOptionSetId,
          filterName: this.currentConfiguration.name,
          configurationId: this.currentConfiguration.id,
          optionsAndChoices
        }

        if (this.currentConfiguration.windChillData?.filterOid) {
          request.filterOid = this.currentConfiguration.windChillData.filterOid
        }

        return request
      }
    }
  }
</script>

<style lang="scss">
  .windchill-filter {
    margin-top: 110px;
    max-height: 590px;
    display: flex;
    flex-direction: column;

    &--container {
      height: 506px;
      overflow-y: scroll;
      -ms-overflow-style: none;  // IE and Edge
      scrollbar-width: none;  // Firefox

      // Chrome, Safari and Opera
      &::-webkit-scrollbar {
        display: none;
      }
    }

    &--actions {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      align-items: flex-end;
      padding: 0 24px 24px;
      gap: 16px;
      width: 100%;
      height: 84px;
      box-shadow: 0 8px 16px rgba(0, 0, 0, .24);
    }
  }
</style>
